
import React from 'react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { HelpCircle, MessageCircle, Book, Info } from 'lucide-react';

const FAQSection = () => {
  return (
    <section className="py-16 bg-white">
      <div className="container mx-auto px-6">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif font-semibold text-patrimoine-dark mb-4">
            Questions fréquentes
          </h2>
          <p className="text-patrimoine-muted text-lg max-w-2xl mx-auto">
            Retrouvez les réponses aux questions les plus posées sur nos services et produits d'investissement écologique.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-5xl mx-auto">
          {/* Support FAQ */}
          <div>
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 rounded-full bg-patrimoine-gold/20">
                <MessageCircle className="h-6 w-6 text-patrimoine-gold" />
              </div>
              <h3 className="text-2xl font-serif text-patrimoine-dark">Support & Assistance</h3>
            </div>
            <Accordion type="single" collapsible className="border-t">
              <AccordionItem value="support-1">
                <AccordionTrigger>Comment contacter votre service client ?</AccordionTrigger>
                <AccordionContent>
                  Notre équipe est disponible du lundi au vendredi de 9h à 18h par téléphone au 01 23 45 67 89,
                  par email à contact@ecopark-invest.com ou via le formulaire de contact sur notre site web.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="support-2">
                <AccordionTrigger>Quel est le délai de réponse à mes questions ?</AccordionTrigger>
                <AccordionContent>
                  Nous nous engageons à répondre à toutes les demandes dans un délai maximum de 48 heures ouvrées.
                  Pour les questions urgentes, privilégiez le contact téléphonique pour une réponse immédiate.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="support-3">
                <AccordionTrigger>Comment se déroule l'accompagnement personnalisé ?</AccordionTrigger>
                <AccordionContent>
                  Chaque investisseur bénéficie d'un conseiller dédié qui l'accompagne tout au long de son parcours, 
                  de l'étude de projet à la mise en place de la stratégie d'investissement, en passant par le suivi 
                  régulier de ses performances.
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>

          {/* Products FAQ - Updated to focus on Livret Gold */}
          <div>
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 rounded-full bg-patrimoine-gold/20">
                <Book className="h-6 w-6 text-patrimoine-gold" />
              </div>
              <h3 className="text-2xl font-serif text-patrimoine-dark">Le Livret Gold</h3>
            </div>
            <Accordion type="single" collapsible className="border-t">
              <AccordionItem value="product-1">
                <AccordionTrigger>Qu'est-ce que le Livret Gold exactement ?</AccordionTrigger>
                <AccordionContent>
                  Le Livret Gold est une solution d'épargne premium adossée à l'or physique, offrant à la fois sécurité et rendements attractifs.
                  Il s'agit d'un placement qui vous permet d'investir dans l'or sans les contraintes de stockage ou d'assurance,
                  tout en bénéficiant de sa valeur refuge face à l'inflation et aux crises économiques.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="product-2">
                <AccordionTrigger>Quels sont les différents packs d'investissement du Livret Gold ?</AccordionTrigger>
                <AccordionContent>
                  Le Livret Gold propose trois packs adaptés à vos objectifs d'investissement : le Pack Standard (10 000€ à 49 999€) 
                  offrant un rendement de 8,12% brut/an, le Pack Premium (50 000€ à 199 999€) avec un rendement de 9,22% brut/an, 
                  et le Pack Gold (200 000€ à 1M€) proposant un rendement exceptionnel de 11,09% brut/an.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="product-3">
                <AccordionTrigger>Pourquoi l'or est-il considéré comme une valeur refuge ?</AccordionTrigger>
                <AccordionContent>
                  L'or est historiquement reconnu comme une valeur refuge car il conserve sa valeur même en période de crise économique, 
                  d'inflation élevée ou d'instabilité monétaire. Contrairement aux monnaies fiduciaires qui peuvent être dévaluées, 
                  l'or a une valeur intrinsèque qui résiste au temps. Notre Livret Gold vous permet de bénéficier de cette stabilité 
                  tout en générant des rendements réguliers.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="product-4">
                <AccordionTrigger>Comment est garantie la sécurité de mon investissement ?</AccordionTrigger>
                <AccordionContent>
                  Votre investissement dans le Livret Gold est sécurisé par de l'or physique stocké dans des coffres-forts hautement 
                  sécurisés en Suisse. Chaque gramme d'or est certifié, assuré et audité régulièrement par des organismes indépendants. 
                  De plus, votre capital bénéficie d'une garantie contractuelle, vous assurant une liquidité totale et un accès à vos fonds 
                  à tout moment sans pénalité.
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </div>

        {/* Additional General FAQ */}
        <div className="mt-16 max-w-5xl mx-auto">
          <div className="flex items-center gap-3 mb-6 justify-center">
            <div className="p-3 rounded-full bg-patrimoine-gold/20">
              <HelpCircle className="h-6 w-6 text-patrimoine-gold" />
            </div>
            <h3 className="text-2xl font-serif text-patrimoine-dark">Questions générales</h3>
          </div>
          <Accordion type="single" collapsible className="border-t">
            {/* Removed the "Quel est le montant minimum pour investir ?" question */}
            <AccordionItem value="general-2">
              <AccordionTrigger>Quels sont les avantages fiscaux de vos produits ?</AccordionTrigger>
              <AccordionContent>
                Nos produits offrent différents avantages fiscaux : Les EcoParks permettent de bénéficier d'une 
                fiscalité avantageuse sur les revenus locatifs et d'amortissements spécifiques aux investissements écologiques. 
                L'assurance-vie luxembourgeoise permet une optimisation fiscale et successorale, notamment grâce aux 
                avantages du contrat de capitalisation.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="general-3">
              <AccordionTrigger>Comment sont sécurisés mes investissements ?</AccordionTrigger>
              <AccordionContent>
                La sécurité est notre priorité absolue. Pour les EcoParks, vous devenez propriétaire d'un actif tangible 
                enregistré au cadastre. Nos produits financiers sont garantis par des institutions réglementées au Luxembourg 
                et en France, bénéficiant de la protection des organismes de régulation européens et de fonds de garantie.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="general-4">
              <AccordionTrigger>Puis-je récupérer mon capital investi à tout moment ?</AccordionTrigger>
              <AccordionContent>
                Oui, tous nos produits d'investissement, y compris le Livret Gold, vous offrent une liquidité totale.
                Vous pouvez récupérer votre capital à tout moment sans frais de sortie ni pénalité. Pour le Livret Gold
                spécifiquement, le délai de traitement des demandes de retrait est de 72 heures maximum, ce qui vous garantit
                un accès rapide à vos fonds en cas de besoin.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>

        {/* CTA Section */}
        <div className="mt-16 text-center">
          <div className="inline-flex items-center gap-2 mb-4 bg-patrimoine-gold/10 px-4 py-2 rounded-full">
            <Info className="h-5 w-5 text-patrimoine-gold" />
            <span className="text-sm font-medium">Vous avez d'autres questions ?</span>
          </div>
          <h3 className="text-2xl md:text-3xl font-serif font-medium mb-6 text-patrimoine-dark">
            Notre équipe est à votre disposition pour vous renseigner
          </h3>
          <div className="flex flex-wrap gap-4 justify-center">
            <button className="bg-patrimoine-gold text-black px-6 py-3 rounded-md hover:bg-amber-400 font-medium transition-all">
              Prendre rendez-vous
            </button>
            <button className="border border-patrimoine-dark text-patrimoine-dark px-6 py-3 rounded-md hover:bg-patrimoine-dark hover:text-white font-medium transition-all">
              Voir tous nos services
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FAQSection;
